import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import {
  CustomerService,
  CustomerServiceControllerService,
  UserRef,
} from '../../../core/generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { DialogChatComponent } from '../../../shared/dialogs/dialog-chat/dialog-chat.component';
import { DialogPlanStatusComponent } from '../../../shared/dialogs/dialog-plan-status/dialog-plan-status.component';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { AuthCustomService } from '../../../security/auth-custom.service';
import { Router } from '@angular/router';
import { EnumDescriptionPipe } from "../../../shared/pipes/enum-description.pipe";
import { ContainerComponent } from '../../../shared/container/container.component';

@Component({
    selector: 'app-profiles',
    standalone: true,
    imports: [MaterialModule, EnumDescriptionPipe, ContainerComponent],
    templateUrl: './admin-customer-service.component.html',
    styleUrl: './admin-customer-service.component.scss',
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ]
})
export class AdminCustomerServiceComponent implements OnInit {
  authCustomService = inject(AuthCustomService);
  _snackBar = inject(MatSnackBar);
  customerServiceService = inject(CustomerServiceControllerService);
  dialog = inject(MatDialog);
  cd = inject(ChangeDetectorRef);
  router = inject(Router);

  // customer service variable
  customerServices: CustomerService[] = [];
  user: UserRef | undefined;
  @ViewChild(MatTable) table!: MatTable<CustomerService>;
  isLoading = true;
  displayedColumnsCSOpen: string[] = [
    'id',
    'subject',
    'registerDateOpen',
    'status',
    'buttons',
  ];

  displayedColumnsCSClose: string[] = [
    'id',
    'subject',
    'registerDateOpen',
    'registerDateClose',
    'status',
    'buttons',
  ];

  expandedElement: any | null = null;

  /**
   * Extrai o profile e o ID de um texto conforme regras:
   * - Se contiver "perfil PF ID:": profile = 'providerPF', id = valor após "ID: "
   * - Se contiver "perfil PJ ID:": profile = 'providerPJ', id = valor após "ID: "
   */
  certify(text: string, csId: number): void {
    let profile: string | null = null;
    let id: string | null = null;

    // Tenta extrair "perfil PF ID: xxxxx"
    const pfMatch = text.match(/perfil\s+PF\s+ID:\s*([^\s,;.\n]+)/i);
    if (pfMatch) {
      profile = 'providerPF';
      id = pfMatch[1];
    } else {
      // Tenta extrair "perfil PJ ID: xxxxx"
      const pjMatch = text.match(/perfil\s+PJ\s+ID:\s*([^\s,;.\n]+)/i);
      if (pjMatch) {
        profile = 'providerPJ';
        id = pjMatch[1];
      }
    }

    // Validação
    if (!profile || !id) {
      this._snackBar.open(
        'Formato inválido. Use: "perfil PF ID: xxxxx" ou "perfil PJ ID: xxxxx"',
        'Fechar',
        { duration: 5000 }
      );
      return;
    }

    // Navega para a rota certificate-info/:profile/:id/:csId
    if (csId !== undefined && csId !== null) {
      this.router.navigate(['admin/certificate-info', profile, id, csId.toString()]);
    } else {
      this._snackBar.open(
        'ID do atendimento não informado para abrir o certificado',
        'Fechar',
        { duration: 5000 }
      );
    }
  }

  async ngOnInit() {
    this.isLoading = true;
    await this.loadCustomerService();
  }

  //---------Gerenciar Atendimentos ------------

  toggleDetails(row: any): void {
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  async loadCustomerService() {
    this.customerServiceService
      .findAll9()
      .subscribe({
        next: (cs) => {
          this.customerServices = cs || [];
          this.isLoading = false;
          this.cd.detectChanges();
          this.table?.renderRows();
        },
        error: (err) => {
          console.error('Error loading customer services', err);
          this.customerServices = [];
          this.isLoading = false;
          this.cd.detectChanges();
        }
      });
  }

  countNotRead(id: number | undefined) {
    // Funcionalidade de contagem de mensagens não lidas foi removida
    // pois chats não fazem mais parte do modelo CustomerService
    return undefined;
  }

  openDialogChat(custumerServiceId: number | undefined) {
    if (!custumerServiceId) {
      return;
    }
    
    let custumerService = this.customerServices.find(
      (cs) => cs.id == custumerServiceId
    );

    const dialogRef = this.dialog.open(DialogChatComponent, {
      data: {
        customerService: custumerService,
      },
    });

    dialogRef.afterClosed().subscribe();
    this.cd.detectChanges();
  }

  // New: open plan status overview dialog for a given CPF/CNPJ
  planStatusOverview(cpfCnpj?: string | null) {
    if (!cpfCnpj) {
      this._snackBar.open('CPF/CNPJ não informado para consulta de plano', 'Fechar', { duration: 4000 });
      return;
    }

    const dialogRef = this.dialog.open(DialogPlanStatusComponent, {
      data: { cpf: cpfCnpj },
      width: '600px',
      maxWidth: '95vw'
    });

    dialogRef.afterClosed().subscribe(() => {
      // no specific action required on close for now
      this.cd.detectChanges();
    });
  }

  setWasRead(CSId: number) {
    // Método mantido mas sem implementação pois chats não existem mais no modelo
  }

  getCustomerServiceOpen() {
    if (!this.customerServices || this.customerServices.length === 0) {
      return [];
    }
    return this.customerServices.filter((cs) => !cs.registerDateClose);
  }

  getCustomerServiceClose() {
    if (!this.customerServices || this.customerServices.length === 0) {
      return [];
    }
    return this.customerServices.filter((cs) => !!cs.registerDateClose);
  }

  closeCustomerService(cs: CustomerService) {
    cs.registerDateClose = new Date().toISOString();
    cs.status = CustomerService.StatusEnum.CustomerserviceClosed;
    if (cs.id) {
      this.customerServiceService
        .update14(cs.id, cs)
        .subscribe((response) => {
          const index = this.customerServices.findIndex(
            (item) => item.id === cs.id
          );
          if (index !== -1) {
            this.customerServices[index] = response;
            this.cd.detectChanges();
          }

          this._snackBar.open('Atendimento finalizado com sucesso!', 'Fechar', {
            duration: 5000,
          });
        });
    }
  }
}
